import { RecipientSelectionEntity } from "../entities/UIEntities/RecipientSelectionEntity";
import { RECIPIENT_SELECTION_TYPES, SEND_STATUSES, USER_TYPES } from "../enums/Enums";
import { convertUTCToLocalTime, removeDuplicatesInArray } from "./CommonUtils";
import { isUserActiveBySites, isUserSubscribed } from "./UsersUtils";
import { isDummyEmail, isNumber } from "./FormUtils";
import Strings from "../resources/Strings";
import MessagingPage from "../containers/Messaging/MessagingPage.module.css";
import MessagingStatusLabel from "../components/UI/Labels/MessagingStatusLabel/MessagingStatusLabel";

export const getRecipientsSelectionEntities = (data) => {
    let {currentScopeUsers, currentGmtUsers, selectedScopeUserTypes, mailingLists, selectedSites, roles} = data;
    
    // Filter to show only subscribed users
    currentScopeUsers = currentScopeUsers?.filter(user => isUserSubscribed(user?.utronUser)).filter(user => isUserActiveBySites(user, selectedSites, roles));
    currentGmtUsers = currentGmtUsers?.filter(user => isUserSubscribed(user?.utronUser)).filter(user => isUserActiveBySites(user, selectedSites, roles));

    const users = currentScopeUsers;
    const userTypes = selectedScopeUserTypes;

    // If user has both transient and other userType, take the other userType
    // (user will be shown only once according to his extra userType)
    let userTypesRes = currentScopeUsers.map(user => user.utronUser.userToUserTypes);
    userTypesRes = userTypesRes.map(utArr => {
      if (utArr.length > 1) {
        // check to take non-transient user to userType
        return utArr.filter(ut => ut.userTypeId !== USER_TYPES.TRANSIENT);
      }
      
      return utArr;
    }).flat();

    // Get all Users with their UserTypes that relevant to the current scope
    const userToUserTypes = userTypesRes;

    // Divide all UserToUserTypes list to groups of UserTypes (each type will be in separate array)
    var userTypeGroups = Object.values(userToUserTypes.reduce((accumulate, data) => {
        const group = data.userTypeId;
        if (!accumulate[group]) {
            accumulate[group] = [];
        }

        accumulate[group].push(data);
        return accumulate;
    }, {}));

    var recipientSelectionEntityList = [];

    // If mailing lists is passed, will be shown
    if (mailingLists !== null && mailingLists?.length > 0) {
      // For every mailing list - create RecipientSelectionEntity and add it to RecipientSelectionEntityList
      mailingLists.forEach((mailingList, idx) => {
          let recipientSelectionEntity = new RecipientSelectionEntity(++idx, Strings.MAILING_LISTS, RECIPIENT_SELECTION_TYPES.MAILING_LIST, mailingList.id, mailingList.name);
          recipientSelectionEntityList.push(recipientSelectionEntity);
      });
    }

    // For every group (every array of UserType) - iterate over all users in group and create RecipientSelectionEntity and add it to RecipientSelectionEntityList
    userTypeGroups.forEach((userTypeGroup) => {
        userTypeGroup.forEach((userToUserType) => {
            let user = users.find(user => user.utronUser.id === userToUserType.userId);
            let identifierName = getIdentifierName(user);
            let identifierId = user?.utronUser?.id; // userToUserType.userId

            let recipientSelectionEntity = new RecipientSelectionEntity(recipientSelectionEntityList.length + 1, userTypes.find((userType) => userType.id === userTypeGroup[0].userTypeId).type, RECIPIENT_SELECTION_TYPES.USER, identifierId, identifierName);
            recipientSelectionEntityList.push(recipientSelectionEntity);
        });
    });

    // For every other group, example: currentGmtUsers
    // filters out admins that also has userToUserTypes
    currentGmtUsers?.filter(user => !userTypesRes.some(ut => ut.userId === user.utronUser.id))?.forEach((otherUser) => {
      let identifierName = getIdentifierName(otherUser);
      let identifierId = otherUser?.utronUser?.id; // userToUserType.userId;

      let recipientSelectionEntity = new RecipientSelectionEntity(recipientSelectionEntityList.length + 1, RECIPIENT_SELECTION_TYPES.OTHER, RECIPIENT_SELECTION_TYPES.OTHER, identifierId, identifierName);
      recipientSelectionEntityList.push(recipientSelectionEntity);
    });

    return recipientSelectionEntityList;
};

export const getMailingListData = (mailingLists, selectedSite) => {
  let firstDate = mailingLists?.length > 0 ? new Date(new Date(convertUTCToLocalTime(mailingLists?.[0]?.createdDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone })) : null;
  let localMinDate = firstDate; 
  let localMaxDate = firstDate;

  let arrangedMailingLists = mailingLists.map(mailingList => {
    const mailingListToUsers = mailingList?.mailingListToUsers;
    let currentDateString = new Date(convertUTCToLocalTime(mailingList.createdDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone });
    let currentDate = new Date(currentDateString); // set converted string as date object
    localMinDate = currentDate < localMinDate ? currentDate : localMinDate;
    localMaxDate = currentDate > localMaxDate ? currentDate : localMaxDate;

    let mlObj = {
      id: mailingList.id,
      name: mailingList.name,
      dateCreated: currentDateString,
      createdBy: mailingList.createdBy,
      user: mailingList.user,
      site: [selectedSite],
      classes: MessagingPage,
      mailingListToUsers: mailingListToUsers,
      size: mailingListToUsers?.length
    }

    return mlObj;
  });

  return {
    mailingLists: arrangedMailingLists,
    minDate: localMinDate,
    maxDate: localMaxDate
  };
};

// Campaign
export const getMessagingCampaignsData = (campaigns, selectedSite) => {
  // take the firstDate to compare with, as the first undeleted campaign
  let firstDate = (campaigns?.length > 0 && campaigns.filter(campaign => !campaign.deleted)?.length > 0 ) ? new Date(new Date(convertUTCToLocalTime(campaigns.filter(campaign => !campaign.deleted)?.[0]?.createdDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone })) : null;
  let localMinDate = firstDate; 
  let localMaxDate = firstDate;

  // arrangedCampaigns (contains both deleted and not deleted campaigns)
  let arrangedCampaigns = campaigns.map(campaign => {
    let currentDateString = new Date(convertUTCToLocalTime(campaign.createdDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone });
    let currentDate = new Date(currentDateString); // set converted string as date object

    // minData / maxDate values will only relate to campaigns that will be shown on ui and are not deleted
    if (!campaign.deleted) {
      localMinDate = currentDate < localMinDate ? currentDate : localMinDate;
      localMaxDate = currentDate > localMaxDate ? currentDate : localMaxDate;
    }

    let campaignObj = {
      id: campaign.id,
      name: campaign.name,
      campaignType: campaign.type, 
      createdDate: currentDateString,
      createdBy: campaign.createdBy,
      user: campaign.user,
      subject: campaign.subject,
      body: campaign.body,
      link: campaign.link,
      attachment: campaign.attachment,
      attachmentName: campaign.attachmentName,
      siteId: campaign.siteId,
      site: [selectedSite],
      classes: MessagingPage,
      deleted: campaign.deleted
    };

    return campaignObj;
  });

  return {
    deletedCampaigns: arrangedCampaigns?.filter(c => c.deleted),
    campaigns: arrangedCampaigns?.filter(c => !c.deleted), 
    minDate: localMinDate,
    maxDate: localMaxDate
  };
};

// Messaging histories
export const getMessagingHistoriesData = (messagingHistories, campaigns, deletedRecipientsIds, selectedSite) => {
  let firstDate = messagingHistories?.length > 0 ? new Date(new Date(convertUTCToLocalTime(messagingHistories?.[0]?.dateSent))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone })) : null;
  let localMinDate = firstDate; 
  let localMaxDate = firstDate;
  
  let arrangedMessagingHistories = messagingHistories.map(mh => {
    let currentDateString = new Date(convertUTCToLocalTime(mh.dateSent))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone });
    let currentDate = new Date(currentDateString); // set converted string as date object
    localMinDate = currentDate < localMinDate ? currentDate : localMinDate;
    localMaxDate = currentDate > localMaxDate ? currentDate : localMaxDate;

    // Find & attach associated campaign if founded
    let foundedCampaignForMessagingHistory = campaigns?.find(c => c.id === mh.campaignId);

    // Find if there is any recipients that are founded on deletedRecipients if yes attach the deletedRecipient
    const recipients = JSON.parse(mh.recipients);
    const campaignInfo = JSON.parse(mh.campaignInfo);
    const deletedRecipients = recipients?.filter(recipient => deletedRecipientsIds?.some(id => id === recipient.UtronUserId));

    let mhObj = {
      id: mh.id,
      dateSent: currentDateString,
      sentBy: mh.sentBy,
      user: mh.user,
      recipients: recipients, 
      campaignInfo: campaignInfo,
      deletedRecipients: deletedRecipients,
      total: mh.total,
      numberOfRecipients: mh.recipients !== null ? recipients?.length : 0,
      site: [selectedSite],
      classes: MessagingPage,
      campaignId: mh.campaignId,
      ...(foundedCampaignForMessagingHistory !== null) && { campaign: foundedCampaignForMessagingHistory}, // populates campaign if exists
      ...(foundedCampaignForMessagingHistory !== null) && { campaignType: foundedCampaignForMessagingHistory.campaignType} // attach for filter prop
    };

    return mhObj;
  });

  return {
    messagingHistories: arrangedMessagingHistories,
    minDate: localMinDate,
    maxDate: localMaxDate
  };
};

// Receives allIds which are all the known and stored users, messaging histories
// and returns all deleted recipients ids from all the messaging histories (if any)
export const getDeletedRecipientsIds = (allIds, messagingHistories) => {
  // if there is any deleted users on any messagingHistories versus allIds
  const allRecipients = removeDuplicatesInArray(messagingHistories?.map(mh => JSON.parse(mh.recipients))?.flat()?.map(recipient => recipient.UtronUserId));
  const deletedRecipientsIds = allRecipients?.filter(mhrId => !allIds?.some(id => id === mhrId));

  return deletedRecipientsIds;
};

// Mailing List Parkers modal (s)
export const getMessagingParkersData = (users) => {
  let arrangedParkers = users.map((user, idx) => {
    
    let userObj = {
      id: ++idx,
      name: `${user.utronUser.firstName} ${user.utronUser.lastName}`,
      email: user.email,
      phoneNumber: user.phoneNumber,
      type: user.utronUser.userToUserTypes?.length > 0 ? user.utronUser.userToUserTypes?.[0]?.userType?.type : RECIPIENT_SELECTION_TYPES.OTHER
    };

    return userObj;
  });

  return arrangedParkers;
};

// Recipients Parkers modal - (used for Valid / Invalid recipients table)
export const getRecipientsParkersData = (users) => {
  let arrangedParkers = users.map((user, idx) => {
    const { name, email, phoneNumber } = getUserContactDetails(user);

    let userObj = {
      id: ++idx,
      name: name,
      email: email,
      phoneNumber: phoneNumber,
      type: user.utronUser?.deleted ? Strings.DELETED : user.utronUser?.userToUserTypes?.length === 0 ? RECIPIENT_SELECTION_TYPES.OTHER : user.utronUser?.userToUserTypes?.find(ut => ut.userTypeId !== USER_TYPES.TRANSIENT)?.userType?.type === undefined ? user.utronUser.userToUserTypes?.[0]?.userType?.type : user.utronUser?.userToUserTypes?.find(ut => ut.userTypeId !== USER_TYPES.TRANSIENT)?.userType?.type,
      status: getMessagingStatusLabel(user, email, phoneNumber), // user?.sendStatus ? user?.sendStatus : getReason(email, phoneNumber)
      subscriptionStatus: user?.sendStatus ? user?.sendStatus : user.utronUser.userAdditionalData.length > 0 ? user.utronUser.userAdditionalData[0].isMessagingEnabled ? `${Strings.SUBSCRIBED}` : `${Strings.UNSUBSCRIBED}` : `${Strings.SUBSCRIBED}`, /* users without userAdditonalData will be shown as Subscribed */ /*"N/A"*/
      reason: getMessagingStatusLabel(user, email, phoneNumber) // user?.sendStatus ? user?.sendStatus : getReason(email, phoneNumber)
    };

    return userObj;
  });

  return arrangedParkers;
};

// Receives current users and a messaging history, used for displaying the users on messaging history area
// takes the messaging history recipients and maps them to utron user
export const getMessagingHistoryUsers = (currentUsers, messagingHistory) => {
  // Finds the recipient from the currentUsers and maps to utron user with additional sendStatus property
  const messagingHistoryUsers = messagingHistory?.recipients?.map(r => {
    const foundedUser = currentUsers?.find(user => user?.utronUser.id === r?.UtronUserId) && {...currentUsers?.find(user => user?.utronUser.id === r?.UtronUserId), sendStatus: r?.Status, email: r?.Email, phoneNumber: r?.PhoneNumber};
    if (foundedUser) {
      return foundedUser;
    }
    else {
      // case when not founded / deleted user
      return {
        "id": r?.UserIdentityId,
        "phoneNumber": r?.PhoneNumber,
        "email": r?.Email,
        "sendStatus": r?.Status,
        "utronUser": {
          "id": r?.UtronUserId,
          "firstName": null,
          "lastName": null,
          "userToUserTypes": [],
          "deleted": true
        }
      };
    }
  });

  return messagingHistoryUsers;
};

// Receives all the recipients types (mailing list, recipient , other) and converts to users
// recipients - RecipientSelectionEntity
export const getUsersByRecipients = ({recipients, mailingLists, currentUsers}) => {
  // identify which is mailing list / single users
  const selectedMailingLists = [];
  const selectedUsersIds = [];

  recipients.forEach(item => {
    if (item?.identifierType === RECIPIENT_SELECTION_TYPES.MAILING_LIST) {
      const foundedMailingList = mailingLists?.find(ml => ml?.id === item?.identifierId);
      selectedMailingLists.push(foundedMailingList);
    }
    else {
      const foundedUserId = currentUsers.find(user => user.utronUser?.id === item.identifierId);
      selectedUsersIds.push(foundedUserId);
    }
  });

  const usersFromMailingLists = selectedMailingLists.map(ml => {
    const users = ml.mailingListToUsers.map(mlto => {
      const foundedUser = currentUsers.find(cu => cu.utronUser?.id === mlto.userId);
      if (foundedUser) {
        return foundedUser;
      }
    }).filter(u => u); // remove undefined

    return users;
  }).flat();

  const singleUsers = selectedUsersIds.map(user => {
    const foundedUser = currentUsers.find(cu => cu.utronUser?.id === user?.utronUser?.id);
      if (foundedUser) {
        return foundedUser;
      }
  }).filter(u => u); // remove undefined

  // concat & distinct
  let allUsers = usersFromMailingLists.concat(singleUsers);
  allUsers = allUsers.filter((user, index) => allUsers.findIndex((us) => us === user) === index)

  return allUsers;
};

// Receives user and checks which IdentifierName to return
// according to user name / email / phone (and if any exists)
export const getIdentifierName = (user) => {
  // in case displaying on selection component recipients that are both subscribed and non subscribed
  // there may be users that doesnt have name (null null) then check if user has one of email / phone
  if (user?.utronUser?.firstName !== null && user?.utronUser?.lastName !== null) {
    return `${user?.utronUser?.firstName} ${user?.utronUser?.lastName}`;
  } 
  else {
    // when both utron user name is null
    // check if email exists / phone number exists - and valid for display
    if (user?.email !== null && !isDummyEmail(user.email)?.isValid) {
      return user.email;
    } 
    if (user?.phoneNumber !== null && isNumber(user.phoneNumber)?.isValid) {
      return user.phoneNumber;
    }
  }
};

// Receives user and returns N/A if any user contact data is not available / invalid
// usage for display on recipients tables
export const getUserContactDetails = (user) => {
  return {
    name: (user.utronUser.firstName !== null && user.utronUser.firstName !== null) ? `${user.utronUser.firstName} ${user.utronUser.lastName}` : `${Strings.NOT_AVAILABLE}`,
    email: (user.email !== null && !isDummyEmail(user.email)?.isValid) ? user.email : `${Strings.NOT_AVAILABLE}`,
    phoneNumber: (user.phoneNumber !== null && isNumber(user.phoneNumber)?.isValid) ? user.phoneNumber : `${Strings.NOT_AVAILABLE}`
  }
};

export const getMessagingStatusLabel = (user, email, phoneNumber) => {
  const reason = getReason(email, phoneNumber);

  if (user?.sendStatus) {
    return <MessagingStatusLabel text={user?.sendStatus} isValid={true} />
  }
  else {
    return <MessagingStatusLabel text={reason} isValid={false} />
  }
};

// Receives email / phone number of user and check reason 
export const getReason = (email, phoneNumber) => {
  let reason = "";

  if (email === Strings.NOT_AVAILABLE) {
    reason = SEND_STATUSES.MISSING_EMAIL;
  }
  if (phoneNumber === Strings.NOT_AVAILABLE) {
    reason = SEND_STATUSES.MISSING_PHONE;
  }

  return reason;
};